
import React, { useEffect } from 'react';
import { Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { RouteConfig, routes } from './routeConfig';
import { PATH_CONSTANTS as PATH } from './pathConstants';
import { useAuth } from '@/contexts/AuthContext';
import { useIntegrations } from '@/hooks/useIntegrations';
import { toast } from '@/components/ui/use-toast';
import NotFound from '@/pages/NotFound';
import { PATHS } from './routePaths';
import ProtectedRoute from '@/components/auth/ProtectedRoute';

interface IntegrationGuardProps {
  requiredIntegration: string;
  children: React.ReactNode;
}

const IntegrationGuard: React.FC<IntegrationGuardProps> = ({ requiredIntegration, children }) => {
  const { integrations, isLoading } = useIntegrations();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Only perform the redirect if we have loaded the integrations data
    if (!isLoading) {
      const isActive = integrations?.[requiredIntegration]?.isActive || false;
      
      if (!isActive) {
        toast({
          title: "Integration Required",
          description: `The ${requiredIntegration} integration must be enabled to access this page.`,
          variant: "destructive"
        });
        
        // Navigate to integrations page or fallback to admin
        const redirectPath = location.pathname.includes(PATH.ADMIN) 
          ? PATH.ADMIN_INTEGRATIONS 
          : PATH.ADMIN;
        
        navigate(redirectPath);
      }
    }
  }, [integrations, requiredIntegration, isLoading, navigate, location.pathname]);

  // Show a loading state while we determine if the integration is active
  if (isLoading) {
    return <div className="flex items-center justify-center p-8">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>;
  }

  const isActive = integrations?.[requiredIntegration]?.isActive || false;
  
  // Don't render children if the integration isn't active (redirect will happen in useEffect)
  if (!isActive) {
    return null;
  }

  return <>{children}</>;
};

// Function to generate routes recursively from our route config
export function renderRoutes() {
  // Render a single route config into a Route component
  function renderRoute(route: RouteConfig) {
    const RouteComponent = route.component;
    
    // Build the element to render based on requirements
    let element = (
      <RouteComponent />
    );
    
    // Apply authentication and permission protection if needed
    if (route.protection === 'auth') {
      element = (
        <ProtectedRoute requiredPermissions={route.meta?.requiredPermissions}>
          {element}  
        </ProtectedRoute>
      );
    }
    
    // Apply integration guard if needed
    if (route.meta?.requiresIntegration) {
      element = (
        <IntegrationGuard requiredIntegration={route.meta.requiresIntegration}>
          {element}
        </IntegrationGuard>
      );
    }

    // If route has children, render them as nested routes
    if (route.children && route.children.length > 0) {
      return (
        <Route key={route.path} path={route.path} element={element}>
          {route.children.map(renderRoute)}
        </Route>
      );
    }

    // Otherwise render a simple route
    return <Route key={route.path} path={route.path} element={element} />;
  }

  // Return a Routes component with all the routes
  return (
    <>
      {routes.map(renderRoute)}
      <Route path="*" element={<NotFound />} />
    </>
  );
}
